import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Button, Spinner, Badge } from 'react-bootstrap';
import { useAppDispatch } from 'redux/store';
import { RootState } from 'redux/store';
import { fetchWorkflowDetails, respondToWorkflowAction } from 'redux/slices/workflowsSlice';

interface Props {
  workflowId: number | string | null;
}

const OperatorMessagePanel = ({ workflowId }: Props) => {
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [respondingId, setRespondingId] = useState<number | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [responseError, setResponseError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const parsedWorkflowId = workflowId ? Number(workflowId) : null;

  const workflow = useSelector((state: RootState) => state.workflows.workflows.find((wf) => wf.workflow_id === parsedWorkflowId));

  useEffect(() => {
    if (!parsedWorkflowId) return;

    setLoading(true);
    setFetchError(null);

    dispatch(fetchWorkflowDetails(parsedWorkflowId))
      .unwrap()
      .catch((err) => {
        console.error('[OperatorMessagePanel] fetch error:', err);
        setFetchError('Failed to load operator message.');
      })
      .finally(() => setLoading(false));
  }, [dispatch, parsedWorkflowId]);

  const handleRespond = async (workflowActionId: number, chosenId: string | number) => {
    setRespondingId(workflowActionId);
    setResponseError(null);
    setSuccessMessage(null);

    try {
      await dispatch(
        respondToWorkflowAction({
          workflow_action_id: workflowActionId,
          chosen_id: chosenId,
        })
      ).unwrap();
      setSuccessMessage('Response submitted successfully.');
      window.dispatchEvent(new CustomEvent('operator-message-responded'));
      // Refresh workflow details to reflect updated state
      if (parsedWorkflowId) {
        dispatch(fetchWorkflowDetails(parsedWorkflowId));
      }
    } catch (err: any) {
      console.error('[OperatorMessagePanel] respond error:', err);
      setResponseError(err?.error || err?.message || 'Failed to submit response.');
    } finally {
      setRespondingId(null);
    }
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2 py-3">
        <Spinner animation="border" size="sm" />
        <span>Loading operator message…</span>
      </div>
    );
  }

  if (fetchError) {
    return <Alert variant="danger">{fetchError}</Alert>;
  }

  if (!workflow) {
    return null;
  }

  const pendingActions = (workflow.workflows_actions ?? []).filter((action) => !action.response);
  const respondedActions = (workflow.workflows_actions ?? []).filter((action) => !!action.response);

  return (
    <div className="mt-3">
      {responseError && (
        <Alert variant="danger" dismissible onClose={() => setResponseError(null)}>
          {responseError}
        </Alert>
      )}
      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <div className="mb-3">
        <strong>Subject:</strong> {workflow.workflow_subject}
      </div>
      {pendingActions.map((action) => (
        <div key={action.workflow_action_id} className="border rounded p-3 mb-3">
          <div className="mb-1">
            <strong>{action.subject}</strong>
          </div>
          {action.message && (
            <p className="text-muted mb-3" style={{ whiteSpace: 'pre-wrap' }}>
              {action.message}
            </p>
          )}
          {action.response_options && action.response_options.length > 0 ? (
            <div className="d-flex flex-wrap gap-2">
              {action.response_options.map((option) => (
                <Button
                  key={option.id}
                  variant="outline-primary"
                  size="sm"
                  disabled={respondingId === action.workflow_action_id}
                  onClick={() => handleRespond(action.workflow_action_id, option.id)}
                >
                  {respondingId === action.workflow_action_id && <Spinner animation="border" size="sm" className="me-2" />}
                  {option.label}
                </Button>
              ))}
            </div>
          ) : (
            <span className="text-muted fst-italic">No response options available.</span>
          )}
        </div>
      ))}
      {respondedActions.map((action) => (
        <div key={action.workflow_action_id} className="border rounded p-3 mb-3 bg-light">
          <div className="mb-1 d-flex align-items-center gap-2">
            <strong>{action.subject}</strong>
            <Badge bg="success">Responded</Badge>
          </div>
          {action.message && (
            <p className="text-muted mb-2" style={{ whiteSpace: 'pre-wrap' }}>
              {action.message}
            </p>
          )}
          <div className="text-muted">
            <span className="fw-medium">Response: </span>
            {action.response?.label ?? action.response?.id}
            {action.response?.user && <span className="ms-2 fst-italic text-secondary">— {action.response.user}</span>}
          </div>
        </div>
      ))}
    </div>
  );
};

export default OperatorMessagePanel;
